import { panelNavHtml } from '../panel-nav-html.js';
import type { WorkflowDiagramPayload, WorkflowDiagramNode } from '../control-events.js';

const COLUMN_HEADERS = ['PAIN', 'BRIDGE', 'OUTCOME'] as const;

/** Options for {@link WorkflowDiagramThreeColumn}. */
export interface WorkflowDiagramOptions {
  /**
   * Docked mode (Intake): renders inside a right-hand column instead of a
   * full-screen overlay. No Back button, no backdrop click-to-close — only
   * an explicit × close control. Uses `root.hidden` for show/hide instead
   * of the overlay's `visible` class toggle. Default mode (Sales) is
   * unchanged: full-screen overlay, `panelNavHtml()` nav (Back + Close),
   * backdrop-click-to-close, `visible` class toggle.
   */
  docked?: boolean;
}

/** Close-only nav row markup for docked mode: same btn-icon/icon markup panelNavHtml() uses for its close button, copied inline (no Back). */
const dockedNavHtml = (): string => `
    <div class="panel-nav panel-nav--close-only">
      <button class="btn-icon nav-close" data-action="close" aria-label="Close workflow sketch">
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.71 2.88 18.3 9.18 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z"/>
        </svg>
      </button>
    </div>`;

/**
 * Three-column animated workflow diagram (Pain → Bridge → Outcome).
 * Mirrors the ServiceDiagram pattern: panel nav, click-to-close, staged
 * reveal via setTimeout. Each column fades in (~800ms beat) and the
 * inter-column arrow draws ~400ms after the preceding column lands.
 *
 * XSS-safe: all text payload fields are HTML-escaped before insertion.
 */
export class WorkflowDiagramThreeColumn {
  private readonly root: HTMLElement;
  private readonly docked: boolean;
  private timers: number[] = [];

  constructor(root: HTMLElement, options: WorkflowDiagramOptions = {}) {
    this.root = root;
    this.docked = !!options.docked;
    if (this.docked) {
      this.root.classList.add('diagram-docked', 'diagram-3col');
      this.root.hidden = true;
    } else {
      this.root.classList.add('diagram-overlay', 'diagram-3col');
    }
    this.root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (this.docked) {
        // Docked: only the explicit × close control closes. No backdrop
        // click-to-close (there is no backdrop), no Back, no nav events.
        if (t.closest('[data-action="close"]')) {
          this.hide();
        }
        return;
      }
      if (t === this.root || t.closest('[data-action="close"]')) {
        this.hide();
        document.dispatchEvent(new CustomEvent('merlin:nav-close', { bubbles: true }));
        return;
      }
      if (t.closest('[data-action="back"]')) {
        document.dispatchEvent(new CustomEvent('merlin:nav-back', { bubbles: true }));
      }
    });
  }

  show(payload: WorkflowDiagramPayload) {
    this.clearTimers();
    const cols: WorkflowDiagramNode[][] = [payload.pain, payload.bridge, payload.outcome];

    const renderCol = (nodes: WorkflowDiagramNode[], ci: number): string => `
      <div class="diagram-col" data-step="${ci}">
        <div class="diagram-col-header">${COLUMN_HEADERS[ci]}</div>
        <div class="diagram-col-body">
          ${nodes
            .map(
              (n) => `
            <div class="diagram-col-node">
              <div class="diagram-col-node-label">${escape(n.label)}</div>
              ${n.sub ? `<div class="diagram-col-node-sub">${escape(n.sub)}</div>` : ''}
            </div>
          `,
            )
            .join('')}
        </div>
      </div>
    `;

    const arrow = `<div class="diagram-col-arrow"><svg viewBox="0 0 40 12" aria-hidden="true"><line x1="0" y1="6" x2="32" y2="6" stroke="currentColor" stroke-width="1.5"/></svg></div>`;
    const colsHtml = cols.map(renderCol).join(arrow);

    this.root.innerHTML = `
      <div class="diagram-sheet">
        ${this.docked ? dockedNavHtml() : panelNavHtml()}
        <div class="diagram-header">
          <div>
            <div class="diagram-eyebrow">Custom workflow sketch</div>
            <div class="diagram-title">${escape(payload.title)}</div>
            <div class="diagram-sub">${escape(payload.subtitle)}</div>
          </div>
        </div>
        <div class="diagram-3col-grid">${colsHtml}</div>
        <div class="diagram-footnote">Generated for this conversation. The actual scope is set during the briefing.</div>
      </div>
    `;
    if (this.docked) {
      this.root.hidden = false;
    } else {
      this.root.classList.add('visible');
    }

    // Staged reveal: ~800ms per beat. Column i appears, then its outbound
    // arrow draws to col i+1.
    const colEls = this.root.querySelectorAll<HTMLElement>('.diagram-col');
    const arrowEls = this.root.querySelectorAll<HTMLElement>('.diagram-col-arrow');
    colEls.forEach((c, i) =>
      this.timers.push(window.setTimeout(() => c.classList.add('drawn'), 100 + i * 800)),
    );
    arrowEls.forEach((a, i) =>
      this.timers.push(window.setTimeout(() => a.classList.add('drawn'), 500 + i * 800)),
    );
  }

  clear(): void {
    this.hide();
    this.root.innerHTML = '';
  }

  hide() {
    if (this.docked) {
      this.root.hidden = true;
    } else {
      this.root.classList.remove('visible');
    }
    this.clearTimers();
  }

  private clearTimers() {
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
  }
}

function escape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
